import { useCallback, useEffect, useRef, useState } from 'react'
import { MessageBubble, type FailedAction, type UnsentMessage } from '@/entities/conversation'
import type { TimelineMessage } from '@/shared/api'
import { inView, useOutboxRoot } from '../model/outbox-shown'
import {
  isRefusal,
  mayHaveArrived,
  retryable,
  unsentAnnouncement,
  unsentLine,
  unsentTime,
} from '../model/unsent'
import type { MessageSend } from '../model/use-message-send'
import s from './PendingMessages.module.css'

/** Действия упавшего: повторить тем же ключом можно — «Повторить», нельзя — «Править» под новый. */
function failedActions(item: UnsentMessage, send: MessageSend): FailedAction[] {
  // Отказ доказывает, что не ушло; иначе, если какая-то попытка могла дойти, — сомнение.
  const doubt = !isRefusal(item) && mayHaveArrived(item.failures)
  const dismiss: FailedAction = {
    label: 'Убрать',
    run: () => send.dismiss(item.idempotencyKey),
    hint: doubt
      ? 'Текст пропадёт насовсем. Если сообщение дошло, гость его уже получил; если нет — не получит.'
      : undefined,
  }
  if (retryable(item, send.now)) {
    return [{ label: 'Повторить', run: () => send.retry(item.idempotencyKey) }, dismiss]
  }
  const rewrite: FailedAction = {
    label: 'Править',
    run: () => send.rewrite(item.idempotencyKey),
    hint: doubt ? 'Уйдёт новым сообщением: если первое дошло, гость получит два — сперва проверьте ленту.' : undefined,
  }
  return [rewrite, dismiss]
}

/**
 * Хвост ленты: сообщения, которых на сервере ещё (или уже) нет — те, что в очереди отправки.
 * Уезжает в слот `feedEnd` треда, то есть рисуется ВНУТРИ скролл-контейнера, последним элементом
 * ленты. Макет кладёт отправленные в рантайме сообщения вне скролла, но это его собственный
 * костыль (в статический список нельзя дописать), а не дизайн.
 *
 * Пузыри — те же самые, что у ленты (`MessageBubble` из `entities/conversation`): он уже умеет и
 * `sending` (приглушён), и `failed` (красная строка под пузырём). Своя копия пузыря разъехалась бы
 * с лентой при первой же правке макета.
 *
 * Действия у упавшего есть ТОЛЬКО здесь: повтор обязан уйти с тем же ключом идемпотентности, а ключ
 * есть только у попытки отправки. У сообщения, которое сервер отдал со статусом `failed`, ключа нет
 * и взять его неоткуда.
 */
export function PendingMessages({ send }: { send: MessageSend }) {
  // Пока строка отказа видна, отказ говорит она, а не тост.
  const register = useOutboxRoot(send.conversationId)
  const rootEl = useRef<Element | null>(null)
  const root = useCallback(
    (el: Element | null) => {
      rootEl.current = el
      const unregister = register(el)
      return () => {
        rootEl.current = null
        unregister?.()
      }
    },
    [register],
  )

  /*
   * Читалке отказ объявляет `<output>` — там же, где его видно глазами: строку не видно, и говорит
   * тост. Объявляется ПЕРЕХОД в «упало», замеченный в этой переписке: состояние («все упавшие»)
   * пересказывало бы старые отказы после каждой правки очереди. Каждое объявление — новый узел
   * (`turn`): область объявляет ДОБАВЛЕННОЕ, и отказ прежними словами иначе бы не прозвучал.
   */
  const [announcement, setAnnouncement] = useState({ text: '', turn: 0 })
  const seen = useRef<{ conversationId: string; statuses: Map<string, UnsentMessage['status']> } | null>(null)
  useEffect(() => {
    const before = seen.current?.conversationId === send.conversationId ? seen.current.statuses : null
    seen.current = {
      conversationId: send.conversationId,
      statuses: new Map(send.queue.map((item) => [item.idempotencyKey, item.status])),
    }
    if (!before) {
      // Своя переписка впервые — её упавшее не пересказываем, объявление прежней гасим.
      setAnnouncement((current) => (current.text ? { ...current, text: '' } : current))
      return
    }
    const visible = (key: string) => {
      const item = rootEl.current?.querySelector(`[data-unsent="${key}"]`)
      return item ? inView(item) : false
    }
    const fresh = send.queue.filter(
      (item) =>
        item.status === 'failed' &&
        before.get(item.idempotencyKey) !== 'failed' &&
        visible(item.idempotencyKey),
    )
    const text = fresh.map((item) => unsentAnnouncement(item, send.now)).join(' ')
    setAnnouncement((current) => ({ text, turn: current.turn + 1 }))
  }, [send.queue, send.conversationId, send.now])

  return (
    <div ref={root} className={s.contents}>
      {send.queue.map((item) => {
        const failedNow = item.status === 'failed'
        const message: TimelineMessage = {
          type: 'message',
          id: item.idempotencyKey,
          direction: 'out',
          text: item.text,
          timeLabel: unsentTime(item.sentAt),
          deliveryStatus: item.status,
          statusLine: failedNow ? unsentLine(item, send.now) : null,
        }

        return (
          <div key={item.idempotencyKey} data-unsent={item.idempotencyKey} className={s.contents}>
            <MessageBubble message={message} actions={failedNow ? failedActions(item, send) : []} />
          </div>
        )
      })}

      <output className={s.srOnly}>
        {announcement.text ? <span key={announcement.turn}>{announcement.text}</span> : null}
      </output>
    </div>
  )
}
